<template lang="pug">
.login-form.md-theme-agentiq
  .login-form__card
    h1.login-form__title {{ $t('auth.brand_title') }}
    p.login-form__subtitle {{ $t('auth.login.subtitle') }}

    button.login-form__sso(
      v-if="showSsoButton"
      type="button"
      :disabled="loading || ssoLoading"
      @click="authorizeAndRedirectToSso"
    )
      | {{ ssoLoading ? $t('auth.login.sso_redirecting') : ssoButtonLabel }}

    p.login-form__divider(v-if="showSsoButton") {{ $t('auth.login.divider') }}

    form(@submit.prevent="submit")
      .login-form__field
        input(
          type="email"
          :placeholder="$t('auth.login.email_placeholder')"
          v-model="email"
          autocomplete="username"
          required
        )
      .login-form__field
        input(
          type="password"
          :placeholder="$t('auth.login.password_placeholder')"
          v-model="password"
          autocomplete="current-password"
          required
        )

      p.login-form__error(v-if="errorMessage") {{ errorMessage }}

      button.login-form__submit(type="submit", :disabled="loading || ssoLoading")
        | {{ loading ? $t('auth.login.submitting') : $t('auth.login.submit') }}

    p.login-form__footer
      router-link(to="/auth/forgot-password") {{ $t('auth.login.forgot_password') }}
</template>

<script>
import * as log from 'loglevel';
import {
  initSuperTokens,
  isSuperTokensSso,
  Session,
  EmailPassword,
  ThirdParty,
} from '@/libs/superTokens';

export default {
  name: 'SuperTokensLogin',
  emits: ['authenticated'],
  data() {
    return {
      email: '',
      password: '',
      errorMessage: '',
      loading: false,
      ssoLoading: false,
    };
  },
  computed: {
    showSsoButton() {
      return isSuperTokensSso();
    },
    ssoButtonLabel() {
      return process.env.SUPERTOKENS_SSO.buttonLabel || this.$t('auth.login.sso_button');
    },
    ssoThirdPartyId() {
      return process.env.SUPERTOKENS_SSO.thirdPartyId;
    },
  },
  mounted() {
    const ssoError = this.$route.query.sso_error;
    if (typeof ssoError === 'string' && ssoError.length) {
      this.errorMessage = ssoError;
      this.$router.replace({ path: '/login', query: {} });
    }
  },
  methods: {
    async authorizeAndRedirectToSso() {
      this.errorMessage = '';
      this.ssoLoading = true;

      try {
        initSuperTokens();
        const url = await ThirdParty.getAuthorisationURLWithQueryParamsAndSetState({
          thirdPartyId: this.ssoThirdPartyId,
          frontendRedirectURI: `${window.location.origin}/auth/callback`,
        });
        window.location.assign(url);
      } catch (err) {
        log.debug('auth: SuperTokens SSO redirect failed', err);
        this.errorMessage = this.$t('auth.login.error_sso_start');
        this.ssoLoading = false;
      }
    },

    async submit() {
      this.errorMessage = '';
      this.loading = true;

      try {
        initSuperTokens();
        const response = await EmailPassword.signIn({
          formFields: [
            { id: 'email', value: this.email },
            { id: 'password', value: this.password },
          ],
        });

        if (response.status === 'OK') {
          const accessToken = await Session.getAccessToken();
          log.debug('auth: SuperTokens login successful');
          this.$emit('authenticated', accessToken);
          return;
        }

        if (response.status === 'WRONG_CREDENTIALS_ERROR') {
          this.errorMessage = this.$t('auth.login.error_invalid_credentials');
          return;
        }

        if (response.status === 'SIGN_IN_NOT_ALLOWED') {
          this.errorMessage = response.reason || this.$t('auth.login.error_deactivated');
          return;
        }

        if (response.status === 'FIELD_ERROR') {
          this.errorMessage = response.formFields
            .map((field) => field.error)
            .filter(Boolean)
            .join(', ') || this.$t('auth.login.error_invalid_credentials');
          return;
        }

        this.errorMessage = this.$t('auth.login.error_generic');
      } catch (err) {
        log.debug('auth: SuperTokens login failed', err);
        this.errorMessage = this.$t('auth.login.error_generic');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.login-form {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
}

.login-form__card {
  width: 100%;
  max-width: 360px;
  padding: 32px 24px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.login-form__title {
  margin: 0 0 8px;
  font-size: 24px;
  line-height: 32px;
  text-align: center;
}

.login-form__subtitle {
  margin: 0 0 24px;
  color: #666;
  text-align: center;
}

.login-form__sso {
  width: 100%;
  margin-bottom: 16px;
  padding: 10px 16px;
  border: 1px solid #4671b3;
  border-radius: 4px;
  background: #fff;
  color: #4671b3;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.login-form__field {
  margin-bottom: 16px;

  input {
    width: 100%;
    box-sizing: border-box;
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;

    &:focus {
      border-color: #4671b3;
      outline: none;
    }
  }
}

.login-form__divider {
  margin: 0 0 16px;
  color: #666;
  text-align: center;
  font-size: 14px;
}

.login-form__error {
  margin: 0 0 16px;
  color: #c62828;
  font-size: 14px;
}

.login-form__submit {
  width: 100%;
  margin-top: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  background: #4671b3;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.login-form__footer {
  margin: 16px 0 0;
  text-align: center;
  font-size: 14px;
}
</style>
